import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  matIcon,
} from '../../ui';

export interface ConfirmDialogData {
  title: string;
  message: string;
  /** Optional value shown on its own line below the message, e.g. the recipient address. */
  highlight?: string;
  /** Optional sentence below the highlighted value. */
  note?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'primary' | 'warn';
  /** Material icon name (legacy) — mapped to Lucide. */
  icon?: string;
}

@Component({
  selector: 'cmt-confirm-dialog',
  standalone: true,
  imports: [...HLM_DIALOG_SHELL, HlmBtnDirective, HlmIconComponent],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge [tone]="badgeTone">
          <hlm-icon [name]="iconName" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>{{ data.title }}</h2>
        </div>
      </hlm-dialog-header>

      <div hlmDialogBody>
        <p class="text-base leading-relaxed text-foreground">{{ data.message }}</p>
        @if (data.highlight) {
          <p class="mt-2 break-all rounded-lg border border-border/50 bg-muted/50 px-3 py-2 text-base font-medium text-foreground"
             data-testid="confirm-dialog-highlight">{{ data.highlight }}</p>
        }
        @if (data.note) {
          <p class="mt-3 text-base leading-relaxed text-foreground">{{ data.note }}</p>
        }
      </div>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close(false)">
          {{ data.cancelLabel || 'Cancel' }}
        </button>
        <button
          hlmBtn
          [variant]="data.confirmColor === 'warn' ? 'destructive' : 'default'"
          (click)="dialogRef.close(true)"
        >
          {{ data.confirmLabel || 'Confirm' }}
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class ConfirmDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ConfirmDialogComponent, boolean>);
  readonly data: ConfirmDialogData = inject(MAT_DIALOG_DATA);

  protected get iconName(): string {
    return this.data.icon ? matIcon(this.data.icon) : 'lucideCircleHelp';
  }

  protected get badgeTone(): 'primary' | 'destructive' {
    return this.data.confirmColor === 'warn' ? 'destructive' : 'primary';
  }
}
